/**
 * Purchase Type Toggle - Shipping vs Pickup
 * Handles metafield checking, CSV fetching, geolocation, and distance calculation
 */

(function() {
  'use strict';

  // Configuration
  const METAFIELD_NAMESPACE = 'custom';
  const METAFIELD_KEY = 'pick_up_urls';
  const STORAGE_KEY = 'user_location';

  // State
  let storeData = [];
  let userLocation = null;
  let currentToggle = 'shipping';
  let selectedStore = null; // Currently selected store
  const NEARBY_DISTANCE_MILES = 50;

  /**
   * Initialize the purchase type toggle
   */
  function initPurchaseTypeToggle() {
    console.log('[Purchase Toggle] Initializing...');

    // CRITICAL: Move overlay and sidebar to body to avoid stacking context issues
    moveOverlayToBody();

    // CRITICAL: Ensure sidebar is hidden IMMEDIATELY on page load
    ensureSidebarHidden();

    // Check if metafield exists
    const pickUpUrls = getMetafield(METAFIELD_NAMESPACE, METAFIELD_KEY);

    if (!pickUpUrls || pickUpUrls.trim() === '') {
      console.log('[Purchase Toggle] No pickup URLs metafield found, hiding toggle');
      hideToggle();
      return;
    }

    console.log('[Purchase Toggle] Pickup URLs found:', pickUpUrls);

    // Move product form content into shipping panel
    moveFormContent();

    // Fetch and parse CSV data
    fetchStoreData(pickUpUrls)
      .then(data => {
        storeData = data;
        console.log('[Purchase Toggle] Store data loaded:', storeData.length, 'stores');
        showToggle();

        // CRITICAL: Load user location and selected store BEFORE setting up handlers
        // This ensures selectedStore is available when handlers fire
        loadUserLocation();

        // Update the pickup panel text with selected store info
        updateAvailabilityText();

        // Now set up handlers after selectedStore is loaded
        initializeToggleHandlers();
        initializeSidebar();
      })
      .catch(error => {
        console.error('[Purchase Toggle] Failed to load store data:', error);
        hideToggle();
      });
  }

  /**
   * Ensure sidebar is hidden on page load
   * This is CRITICAL - must be called immediately and forcefully
   */
  function ensureSidebarHidden() {
    const overlay = document.getElementById('store-overlay');
    const sidebar = document.getElementById('store-sidebar');

    console.log('[Purchase Toggle] Force-hiding sidebar...');

    if (overlay) {
      overlay.classList.remove('active', 'is--visible');
      overlay.classList.add('is--hidden');
      overlay.style.opacity = '0';
      overlay.style.visibility = 'hidden';
      overlay.style.pointerEvents = 'none';
    }

    if (sidebar) {
      sidebar.classList.remove('active');
      sidebar.style.transform = 'translateX(100%)';
    }

    console.log('[Purchase Toggle] Sidebar force-hidden:', {
      overlayExists: !!overlay,
      sidebarExists: !!sidebar,
      overlayVisible: overlay?.classList.contains('is--visible'),
      sidebarActive: sidebar?.classList.contains('active')
    });
  }

  /**
   * Move overlay and sidebar to body to avoid stacking context issues
   * This ensures they are at the top level of the DOM
   */
  function moveOverlayToBody() {
    const overlay = document.getElementById('store-overlay');
    const sidebar = document.getElementById('store-sidebar');

    if (overlay && overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
      console.log('[Purchase Toggle] Moved overlay to body');
    }

    if (sidebar && sidebar.parentNode !== document.body) {
      document.body.appendChild(sidebar);
      console.log('[Purchase Toggle] Moved sidebar to body');
    }
  }

  /**
   * Move product form content into shipping panel
   */
  function moveFormContent() {
    const shippingPanel = document.getElementById('purchase-panel-shipping');
    const productForm = document.querySelector('.t4s-product-form__variants');

    if (shippingPanel && productForm) {
      console.log('[Purchase Toggle] Moving product form into shipping panel');
      shippingPanel.innerHTML = '';
      shippingPanel.appendChild(productForm);
    }
  }

  /**
   * Get metafield value from product
   */
  function getMetafield(namespace, key) {
    // Try to get from window object (set by Liquid template)
    if (window.productMetafields &&
        window.productMetafields.custom &&
        window.productMetafields.custom.pick_up_urls !== undefined) {
      return window.productMetafields.custom.pick_up_urls;
    }

    return null;
  }

  /**
   * Fetch store data from CSV URL
   */
  async function fetchStoreData(csvUrl) {
    console.log('[Purchase Toggle] Fetching store data from:', csvUrl);

    try {
      const response = await fetch(csvUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const csvText = await response.text();
      console.log('[Purchase Toggle] CSV data fetched, length:', csvText.length);
      const parsed = parseStoreCSV(csvText);

      // If parsing returned empty data, use fallback
      if (parsed.length === 0) {
        console.log('[Purchase Toggle] No stores parsed, using fallback data');
        return getFallbackStoreData();
      }

      return parsed;
    } catch (error) {
      console.error('[Purchase Toggle] Error fetching CSV:', error);

      // Always provide fallback data if fetch fails
      console.log('[Purchase Toggle] Using fallback store data due to error');
      return getFallbackStoreData();
    }
  }

  /**
   * Get fallback store data for testing
   */
  function getFallbackStoreData() {
    return [
      {
        name: 'Los Angeles USC Village',
        latitude: 34.0689,
        longitude: -118.4452,
        storeType: 'target',
        address: '3201 S Hoover St, Los Angeles, CA 90089',
        distance: null
      },
      {
        name: 'Los Angeles Downtown',
        latitude: 34.0522,
        longitude: -118.2437,
        storeType: 'target',
        address: '1 Worlds Fair Dr, Los Angeles, CA 90089',
        distance: null
      },
      {
        name: 'West Hollywood',
        latitude: 34.0699,
        longitude: -118.3897,
        storeType: 'walmart',
        address: '500 S La Brea Ave, Los Angeles, CA 90036',
        distance: null
      },
      {
        name: 'Santa Monica Place',
        latitude: 34.0178,
        longitude: -118.4981,
        storeType: 'target',
        address: '395 Santa Monica Pl, Santa Monica, CA 90401',
        distance: null
      },
      {
        name: 'Burbank Town Center',
        latitude: 34.1808,
        longitude: -118.3449,
        storeType: 'walmart',
        address: '1800 W Empire Ave, Burbank, CA 91504',
        distance: null
      },
      {
        name: 'Pasadena',
        latitude: 34.1478,
        longitude: -118.1445,
        storeType: 'target',
        address: '450 S Colorado Pl, Pasadena, CA 91101',
        distance: null
      }
    ];
  }

  /**
   * Parse store CSV data
   * Expected format: store_name,latitude,longitude,store_type,inventory,address
   */
  function parseStoreCSV(csvText) {
    console.log('[Purchase Toggle] Parsing CSV data:', csvText.substring(0, 200));

    const lines = csvText.trim().split('\n');
    const stores = [];

    // Skip header row, parse data rows
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = parseCSVLine(line);
      console.log(`[Purchase Toggle] Parsing line ${i}:`, values);

      if (values.length >= 4) {
        const store = {
          name: values[0].trim(),
          latitude: parseFloat(values[1]),
          longitude: parseFloat(values[2]),
          storeType: values[3].trim().toLowerCase(),
          address: values.length >= 6 ? values[5].trim() : '',
          distance: null // Will be calculated
        };
        stores.push(store);
      }
    }

    console.log(`[Purchase Toggle] Parsed ${stores.length} stores`);
    return stores;
  }

  /**
   * Parse a CSV line handling quoted fields
   */
  function parseCSVLine(line) {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result.map(value => value.replace(/^"|"$/g, ''));
  }

  /**
   * Show the toggle UI
   */
  function showToggle() {
    const toggleContainer = document.querySelector('.purchase-type-toggle-container');
    if (toggleContainer) {
      toggleContainer.style.display = 'block';
    }
  }

  /**
   * Hide the toggle UI
   */
  function hideToggle() {
    const toggleContainer = document.querySelector('.purchase-type-toggle-container');
    if (toggleContainer) {
      toggleContainer.style.display = 'none';
    }
  }

  /**
   * Initialize toggle event handlers
   */
  function initializeToggleHandlers() {
    const tabs = document.querySelectorAll('.purchase-toggle-card');

    tabs.forEach(tab => {
      tab.addEventListener('click', function() {
        const target = this.dataset.target;
        setActivePanel(target);
      });
    });

    // Check URL params for initial state
    const params = new URLSearchParams(window.location.search);
    const initialView = params.get('purchase_type');
    if (initialView === 'pickup' || initialView === 'shipping') {
      setActivePanel(initialView);
    }
  }

  /**
   * Set active panel
   */
  function setActivePanel(target) {
    currentToggle = target;

    const tabs = document.querySelectorAll('.purchase-toggle-card');
    const panels = document.querySelectorAll('.purchase-panel');

    // If pickup tab clicked without selected store, directly open sidebar
    if (target === 'pickup' && !selectedStore) {
      // Just highlight the pickup tab, open sidebar
      tabs.forEach(tab => {
        const isActive = tab.dataset.target === target;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
      });
      panels.forEach(panel => {
        panel.classList.toggle('active', false);
      });

      // Hide "Frequently Bought Together" when opening sidebar
      const frequentlyBoughtSection = document.querySelector('.frequently-bought-together');
      if (frequentlyBoughtSection) {
        frequentlyBoughtSection.style.display = 'none';
      }

      openSidebar();
      return;
    }

    tabs.forEach(tab => {
      const isActive = tab.dataset.target === target;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });

    panels.forEach(panel => {
      panel.classList.toggle('active', panel.id === `purchase-panel-${target}`);
    });

    // Show/hide "Frequently Bought Together" based on purchase type
    const frequentlyBoughtSection = document.querySelector('.frequently-bought-together');
    if (frequentlyBoughtSection) {
      if (target === 'pickup') {
        frequentlyBoughtSection.style.display = 'none';
      } else {
        frequentlyBoughtSection.style.display = '';
      }
    }

    // Update URL without refresh
    const url = new URL(window.location.href);
    url.searchParams.set('purchase_type', target);
    window.history.replaceState({}, '', url);
  }

  /**
   * Initialize sidebar functionality
   */
  function initializeSidebar() {
    const container = document.querySelector('.purchase-type-toggle-container');
    const overlay = document.getElementById('store-overlay');
    const sidebar = document.getElementById('store-sidebar');
    const btnShow = document.getElementById('btn-show-stores');
    const btnClose = document.getElementById('btn-close-stores');
    const btnEditLocation = document.getElementById('btn-edit-location');
    const locationForm = document.getElementById('store-location-form');
    const btnUseCurrent = document.getElementById('btn-use-current-location');
    const btnLookup = document.getElementById('btn-lookup-location');
    const zipInput = document.getElementById('store-zip-input');

    if (btnShow) {
      btnShow.addEventListener('click', (e) => {
        e.preventDefault();
        openSidebar();
      });
    }

    if (btnClose) {
      btnClose.addEventListener('click', closeSidebar);
    }

    if (overlay) {
      overlay.addEventListener('click', closeSidebar);
    }

    // Handle page overlay click to close sidebar
    const pageOverlay = document.querySelector('.t4s-close-overlay');
    if (pageOverlay) {
      pageOverlay.addEventListener('click', closeSidebar);
    }

    if (btnEditLocation && locationForm) {
      btnEditLocation.addEventListener('click', () => {
        btnEditLocation.classList.toggle('active');
        locationForm.classList.toggle('active');
        // Hide error when toggling location form
        hideZipError();
      });
    }

    if (btnUseCurrent) {
      btnUseCurrent.addEventListener('click', () => {
        getCurrentLocation();
      });
    }

    if (btnLookup && zipInput) {
      btnLookup.addEventListener('click', () => {
        lookupLocation(zipInput.value);
      });

      // Hide error when user starts typing
      zipInput.addEventListener('input', () => {
        hideZipError();
      });

      // Allow Enter key to trigger lookup
      zipInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          lookupLocation(zipInput.value);
        }
      });
    }

    // Handle "More near you" toggle
    // The checkbox enables/disables the 50-mile filter
    // Checked (ON) = show nearby only (within 50 miles)
    // Unchecked (OFF) = show all stores
    const nearbyToggle = document.querySelector('.store-toggle-switch input[type="checkbox"]');
    const toggleContainer = document.querySelector('.store-toggle-switch');
    console.log('[Purchase Toggle] Looking for toggle switch... Found:', !!nearbyToggle);
    console.log('[Purchase Toggle] Toggle container found:', !!toggleContainer);

    if (nearbyToggle) {
      console.log('[Purchase Toggle] Toggle element found! Current state:', nearbyToggle.checked);

      // Use both change and click events for better compatibility
      nearbyToggle.addEventListener('change', (e) => {
        console.log('[Purchase Toggle] Toggle change event fired!');
        const toggleEnabled = e.target.checked;
        console.log('[Purchase Toggle] Toggle changed - "More near you":', toggleEnabled ? 'ENABLED (50-mile filter)' : 'DISABLED (show all)');
        renderStoreList();
      });

      nearbyToggle.addEventListener('click', (e) => {
        console.log('[Purchase Toggle] Toggle click event fired! Checked:', e.target.checked);
      });

      // Log initial state
      console.log('[Purchase Toggle] Toggle initialized - nearby filter:', nearbyToggle.checked ? 'ENABLED' : 'DISABLED');
    } else {
      console.error('[Purchase Toggle] ❌ Toggle element NOT found!');
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sidebar && sidebar.classList.contains('active')) {
        closeSidebar();
      }
    });
  }

  /**
   * Open sidebar
   */
  function openSidebar() {
    console.log('[Purchase Toggle] Opening sidebar...');

    // CRITICAL: Ensure overlay and sidebar are at body level to avoid stacking context issues
    moveOverlayToBody();

    const overlay = document.getElementById('store-overlay');
    const sidebar = document.getElementById('store-sidebar');
    const stickyMedia = document.querySelector('.t4s-product__media-wrapper--sticky');

    if (!overlay || !sidebar) {
      console.error('[Purchase Toggle] Cannot open sidebar - elements not found');
      return;
    }

    // Reset scroll position to top when opening
    sidebar.scrollTop = 0;

    // First, make elements displayable
    overlay.style.display = 'block';
    sidebar.style.display = 'block';

    // Lower z-index of sticky media wrapper to prevent interference
    if (stickyMedia) {
      stickyMedia.style.zIndex = '0';
    }

    // Hide theme's sticky header using theme's utility
    if (window.T4SThemeSP && window.T4SThemeSP.HeaderUtils && window.T4SThemeSP.HeaderUtils.hideAndRestoreStickyHeader) {
      window.T4SThemeSP.HeaderUtils.hideAndRestoreStickyHeader();
      console.log('[Purchase Toggle] Called hideAndRestoreStickyHeader');
    }

    // Add is--visible class to overlay (like theme's t4s-close-overlay behavior)
    overlay.classList.add('is--visible');

    // Re-render store list to ensure distances are shown
    renderStoreList();

    // Small delay to allow display:block to apply before adding active class
    requestAnimationFrame(() => {
      overlay.classList.remove('is--hidden');
      sidebar.classList.add('active');
      document.body.style.overflow = 'hidden';
    });

    console.log('[Purchase Toggle] Sidebar opened');
  }

  /**
   * Close sidebar
   */
  function closeSidebar() {
    console.log('[Purchase Toggle] Closing sidebar...');
    const overlay = document.getElementById('store-overlay');
    const sidebar = document.getElementById('store-sidebar');
    const stickyMedia = document.querySelector('.t4s-product__media-wrapper--sticky');

    if (!overlay || !sidebar) {
      console.error('[Purchase Toggle] Cannot close sidebar - elements not found');
      return;
    }

    // Remove active class and add is--hidden (like theme's t4s-close-overlay behavior)
    sidebar.classList.remove('active');
    overlay.classList.remove('is--visible');
    overlay.classList.add('is--hidden');

    document.body.style.overflow = '';

    // Restore z-index of sticky media wrapper
    if (stickyMedia) {
      // stickyMedia.style.zIndex = '3';
    }

    // After transition completes, hide with display:none
    setTimeout(() => {
      if (!sidebar.classList.contains('active')) {
        sidebar.style.display = 'none';
      }
      if (!overlay.classList.contains('is--visible')) {
        overlay.style.display = 'none';
      }
    }, 350); // Wait for CSS transition to complete

    console.log('[Purchase Toggle] Sidebar closed');
  }

  /**
   * Load saved user location and selected store
   */
  function loadUserLocation() {
    // Load user location
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        userLocation = JSON.parse(saved);
        updateLocationDisplay();
        console.log('[Purchase Toggle] Loaded saved location:', userLocation);
      } catch (e) {
        console.error('[Purchase Toggle] Failed to load saved location:', e);
        userLocation = null;
      }
    } else {
      // Set default location (90089 - Los Angeles area)
      if (window.ZipCodeDatabase && window.ZipCodeDatabase.hasZipCode('90089')) {
        const location = window.ZipCodeDatabase.lookup('90089');
        userLocation = {
          latitude: location.latitude,
          longitude: location.longitude,
          zip: '90089',
          timestamp: Date.now()
        };
        saveUserLocation();
        updateLocationDisplay();
        console.log('[Purchase Toggle] Using default location:', userLocation);
      } else {
        userLocation = null;
      }
    }

    // Load selected store
    const savedStore = localStorage.getItem('selected_store');
    if (savedStore) {
      try {
        const parsedStore = JSON.parse(savedStore);
        console.log('[Purchase Toggle] Loaded selected store:', parsedStore.name);

        // Find the matching store in our store data
        const matchingStore = storeData.find(s => s.name === parsedStore.name);
        if (matchingStore) {
          selectedStore = matchingStore;
          console.log('[Purchase Toggle] Selected store found in data:', selectedStore.name);
        } else {
          console.warn('[Purchase Toggle] Selected store not found in current data:', parsedStore.name);
          selectedStore = null;
        }
      } catch (e) {
        console.error('[Purchase Toggle] Failed to load selected store:', e);
        selectedStore = null;
      }
    } else {
      console.log('[Purchase Toggle] No saved store found');
      selectedStore = null;
    }

    // Render store list with distances
    renderStoreList();
  }

  /**
   * Get current location using Geolocation API
   */
  function getCurrentLocation() {
    // Hide any previous error
    hideZipError();

    if (!navigator.geolocation) {
      showZipError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        userLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: Date.now()
        };
        saveUserLocation();
        updateLocationDisplay();
        renderStoreList();
      },
      (error) => {
        console.error('[Purchase Toggle] Geolocation error:', error);
        showZipError('Unable to get your location. Please enter your ZIP code.');
      }
    );
  }

  /**
   * Lookup location by ZIP code
   */
  async function lookupLocation(zip) {
    // Hide any previous error
    hideZipError();

    if (!zip || zip.trim() === '') {
      showZipError('Please enter a ZIP code');
      return;
    }

    // Clean ZIP code
    const cleanZip = zip.trim().padStart(5, '0');

    console.log('[Purchase Toggle] Looking up ZIP code:', cleanZip);

    // Try local ZIP code database first
    if (window.ZipCodeDatabase && window.ZipCodeDatabase.hasZipCode(cleanZip)) {
      const location = window.ZipCodeDatabase.lookup(cleanZip);
      console.log('[Purchase Toggle] ZIP found in local database:', location);

      userLocation = {
        latitude: location.latitude,
        longitude: location.longitude,
        zip: cleanZip,
        timestamp: Date.now()
      };

      saveUserLocation();
      updateLocationDisplay();
      renderStoreList();

      return;
    }

    // ZIP not found in database
    console.warn('[Purchase Toggle] ZIP code not found in database:', cleanZip);
    showZipError('ZIP code not found. Please enter a valid 5-digit ZIP code.');

    // Still save the ZIP for display, but without coordinates
    userLocation = {
      zip: cleanZip,
      latitude: null,
      longitude: null,
      timestamp: Date.now()
    };

    saveUserLocation();
    updateLocationDisplay();
    renderStoreList();
  }

  /**
   * Save user location to localStorage
   */
  function saveUserLocation() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userLocation));
  }

  /**
   * Update location display in sidebar
   */
  function updateLocationDisplay() {
    const zipDisplay = document.querySelector('.store-zip span');
    if (zipDisplay) {
      zipDisplay.textContent = userLocation.zip || 'ZIP';
    }
  }

  /**
   * Show ZIP code error message
   */
  function showZipError(message) {
    const errorElement = document.getElementById('store-zip-error');
    const errorText = errorElement?.querySelector('.error-text');

    if (errorElement && errorText) {
      errorText.textContent = message;
      errorElement.style.display = 'flex';

      // Auto-hide after 5 seconds
      setTimeout(() => {
        hideZipError();
      }, 5000);

      console.error('[Purchase Toggle] ZIP error:', message);
    }
  }

  /**
   * Hide ZIP code error message
   */
  function hideZipError() {
    const errorElement = document.getElementById('store-zip-error');
    if (errorElement) {
      errorElement.style.display = 'none';
    }
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   */
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // Earth's radius in miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  function toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Render store list with distances
   */
  function renderStoreList() {
    const storeList = document.querySelector('.store-list');
    if (!storeList) {
      console.error('[Purchase Toggle] Store list element not found!');
      return;
    }

    console.log('[Purchase Toggle] renderStoreList called');
    console.log('[Purchase Toggle] Total stores loaded:', storeData.length);
    console.log('[Purchase Toggle] Selected store:', selectedStore ? selectedStore.name : 'None');

    // Check if we have any stores
    if (!storeData || storeData.length === 0) {
      console.warn('[Purchase Toggle] No stores to display!');
      storeList.innerHTML = '<p style="text-align: center; padding: 20px;">No stores available</p>';
      return;
    }

    // Get toggle state FIRST
    const nearbyToggle = document.querySelector('.store-toggle-switch input[type="checkbox"]');
    const nearbyFilterEnabled = nearbyToggle ? nearbyToggle.checked : false;
    console.log('[Purchase Toggle] Nearby filter enabled:', nearbyFilterEnabled, '| Toggle exists:', !!nearbyToggle);

    // Create a copy for display to avoid mutating the original storeData
    let displayStores = [...storeData];

    // Only calculate distances and sort when "More near you" toggle is ON
    if (nearbyFilterEnabled && userLocation && userLocation.latitude && userLocation.longitude) {
      console.log('[Purchase Toggle] Calculating distances from user location:', userLocation);
      displayStores.forEach(store => {
        store.distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          store.latitude,
          store.longitude
        );
      });

      // Sort by distance (on the copy, not the original)
      displayStores.sort((a, b) => a.distance - b.distance);
      console.log('[Purchase Toggle] Stores sorted by distance');

      // Filter to only nearby stores within 50 miles
      displayStores = displayStores.filter(store =>
        store.distance !== null && store.distance <= NEARBY_DISTANCE_MILES
      );
      console.log('[Purchase Toggle] Filtered to', displayStores.length, 'nearby stores (within 50 miles)');
    } else {
      console.log('[Purchase Toggle] Showing stores in original order');
    }

    console.log('[Purchase Toggle] Final store count to render:', displayStores.length);

    // Check if filtering removed all stores
    if (displayStores.length === 0) {
      if (nearbyFilterEnabled && userLocation && userLocation.latitude && userLocation.longitude) {
        storeList.innerHTML = '<p style="text-align: center; padding: 20px;">No stores within 50 miles.<br><br><strong>Try:</strong><br>• Uncheck "More near you" to see all stores<br>• Enter a US ZIP code for your location</p>';
      } else {
        storeList.innerHTML = '<p style="text-align: center; padding: 20px;">No stores available</p>';
      }
      return;
    }

    // Clear existing list
    storeList.innerHTML = '';

    // Render stores
    displayStores.forEach((store, index) => {
      const storeCard = createStoreCard(store);
      storeList.appendChild(storeCard);
    });

    // Update selected card indicator
    updateSelectedCard();
  }

  /**
   * Create store card element
   */
  function createStoreCard(store) {
    const card = document.createElement('div');
    card.className = 'store-card';
    card.addEventListener('click', () => selectStore(store));

    const iconHtml = getStoreIconHtml(store.storeType);
    const distanceText = store.distance ? Math.round(store.distance) + ' miles' : '';

    card.innerHTML = `
      <div class="store-card-main">
        <div class="store-card-icon ${store.storeType}">${iconHtml}</div>
        <div class="store-card-info">
          <div class="store-card-name">${escapeHtml(store.name)}</div>
          ${store.address ? `<div class="store-card-address">${escapeHtml(store.address)}</div>` : ''}
          <div class="store-card-distance">${distanceText}</div>
        </div>
      </div>
    `;
    return card;
  }

  /**
   * Update selected store card indicator
   */
  function updateSelectedCard() {
    const card = document.getElementById('store-selected-card');
    const icon = document.getElementById('store-selected-icon');
    const name = document.getElementById('store-selected-name');
    const address = document.getElementById('store-selected-address');
    const distance = document.getElementById('store-selected-distance');

    if (!card || !name || !distance || !icon) return;

    if (selectedStore) {
      card.style.display = 'flex';
      name.textContent = selectedStore.name;
      if (address) {
        address.textContent = selectedStore.address || '';
        address.style.display = selectedStore.address ? 'block' : 'none';
      }
      distance.textContent = selectedStore.distance ? Math.round(selectedStore.distance) + ' miles' : '';
      icon.className = 'store-selected-card-icon ' + selectedStore.storeType;
      icon.innerHTML = getStoreIconHtml(selectedStore.storeType);
    } else {
      card.style.display = 'none';
    }
  }

  /**
   * Get store icon HTML based on store type
   */
  function getStoreIconHtml(storeType) {
    // Get icon URLs from window object (set by Liquid template)
    const iconUrls = window.storeIconUrls || {
      target: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTAgMTIuNUMxMC42NjMgMTIuNSAxMS4yOTg5IDEyLjIzNjYgMTEuNzY3OCAxMS43Njc4QzEyLjIzNjYgMTEuMjk4OSAxMi41IDEwLjY2MyAxMi41IDEwQzEyLjUgOS4zMzY5NiAxMi4yMzY2IDguNzAxMDcgMTEuNzY3OCA4LjIzMzIzQzExLjI5ODkgNy43NjMzOSAxMC42NjMgNy41IDEwIDcuNUM5LjMzNjk2IDcuNSA4LjcwMTA3IDcuNzYzMzkgOC4yMzIzIDguMjMyMjNDNy43NjMzOSA4LjcwMTA3IDcuNSA5LjMzNjk2IDcuNSAxMEM3LjUgMTAuNjYzIDcuNzYzMzkgMTEuMjk4OSA4LjIzMjMgMTEuNzY3ODMOC43MDEwNyAxMi4yMzY2IDkuMzM2OTYgMTIuNSAxMCAxMi41WiIgZmlsbD0iI0NDMDAwMCIvPjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNNjk5Ljk4NCAxOC4zMzM0QzE0LjYwMjMgMTguMzMzNCAxOC4zMzMyIDE0LjYwMjYgMTguMzMzMiAxMC4wMDAxQzE4LjMzMzIgNS4zOTc1OCAxNC42MDIzIDEuNjY2NzUgOS45OTk4NCAxLjY2Njc1QzUuMzk3MzQgMS42NjY3NSAxLjY2NjUgNS4zOTc1OCAxLjY2NjUgMTAuMDAwMUMxLjY2NjUgMTQuNjAyNiA1LjM5NzM0IDE4LjMzMzQgOS45OTk4NCAxOC4zMzQ0Wk0xNC45OTk4IDEwLjAwMDFDMTQuOTk5OCAxMS4zMjYyIDE0LjQ3MzEgMTIuNTk3OSAxMy41MzU0IDEzLjUzNTZDMTIuNTk3NyAxNC40NzMzIDExLjMyNTkgMTUuMDAwMSA5Ljk5OTg0IDE1LjAwMDFDOC42NzM3NSAxNS4wMDAxIDcuNDAxOTkgMTQuNDczMyA2LjQ2NDMgMTMuNTM1NkM1LjUyNjYyIDEyLjU5NzkgNC45OTk4NCAxMS4zMjYyIDQuOTk5ODQgMTAuMDAwMUM0Ljk5OTg0IDguNjc0MDUgNS41MjY2MiA3LjQwMjIzIDYuNDY0MyA2LjQ2NDU1QzcuNDAxOTkgNS41MjY4NyA4LjY3Mzc1IDUuMDAwMDggOS45OTk4NCA1LjAwMDA4QzExLjMyNTkgNS4wMDAwOCAxMi41OTc3IDUuNTI2ODcgMTMuNTM1NCA2LjQ2NDU1QzE0LjQ3MzEgNy40MDIyMyAxNC45OTk4IDguNjc0IDE0Ljk5OTggMTAuMDAwMVoiIGZpbGw9IiNDQzAwMDAiLz48L3N2Zz4=',
      walmart: 'data:image/svg+xml;base64,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',
      default: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTAgMTIuNUMxMC42NjMgMTIuNSAxMS4yOTg5IDEyLjIzNjYgMTEuNzY3OCAxMS43Njc4QzEyLjIzNjYg11LjI5ODkgMTIuNSAxMC42NjMgMTIuNSAxMEMxMi41IDkuMzM2OTYgMTIuMjM2NiA4LjcwMTA3IDExLjc2NzggOC4yMzIyM0MxMS4yOTg5IDcuNzYzMzkgMTAuNjYzIDcuNSAxMCA3LjVDOS4zMzY5NiA3LjUgOC43MDEwNyA3Ljc2MzM5IDguMjMyMjMgOC4yMzIyM0M3Ljc2MzM5IDguNzAxMDcgNy41IDkuMzM2OTYgNy41IDEwQzcuNSAxMC42NjMgNy43NjMzOSAxMS4yOTg5IDguMjMyMjMgMTEuNzY3ODM4LjcwMTA3IDEyLjIzNjYgOS4zMzY5NiAxMi41IDEwIDEyLjVaIiBmaWxsPSIjQ0MwMDAwIi8+PHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik05Ljk5OTg0IDE4LjMzMzRDMTQuNjAyMyAxOC4zMzM0IDE4LjMzMzIgMTQuNjAyNiAxOC4zMzMyIDEwLjAwMDFDMTguMzMzMiA1LjM5NzU4IDE0LjYwMjMgMS42NjY3NSA5Ljk5OTg0IDEuNjY2NzVDNS4zOTczNCAxLjY2Njc1IDEuNjY2NSA1LjM5NzU4IDEuNjY2NSAxMC4wMDAxQzEuNjY2NSAxNC42MDI2IDUuMzk3MzQgMTguMzMzNCA5Ljk5OTg0IDE4LjMzMzRaTTE0Ljk5OTggMTAuMDAwMUMxNC45OTk4IDExLjMyNjIgMTQuNDczMSAxMi41OTc5IDEzLjUzNTQgMTMuNTM1NkMxMi41OTc3IDE0LjQ3MzMgMTEuMzI1OSAxNS4wMDAxIDkuOTk5ODQgMTUuMDAwMUM4LjY3Mzc1IDE1LjAwMSA3LjQwMTk5IDE0LjQ3MzMgNi40NjQzIDEzLjUzNTZDNS41MjY2MiAxMi41OTc5IDQuOTk5ODQgMTEuMzI2MiA0Ljk5OTg0IDEwLjAwMDFDNC45OTk4NCA4LjY3NDA1IDUuNTI2NjIgNy40MDIyMyA2LjQ2NDMgNi40NjQ1NUM3LjQwMTk5IDUuNTI2ODcgOC42NzM3NSA1LjAwMDA4IDkuOTk5ODQgNS4wMDAwOUMxMS4zMjU5IDUuMDAwMDggMTIuNTk3NyA1LjUyNjg3IDEzLjUzNTQgNi40NjQ1NUMxNC40NzMxIDcuNDAyMjMgMTQuOTk5OCA4LjY3NCAxNC45OTk4IDEwLjAwMDFaIiBmaWxsPSIjQ0MwMDAwIi8+PC9zdmc+'
    };

    const iconUrl = iconUrls[storeType] || iconUrls.default;

    // Return img tag with the SVG file
    return `<img src="${iconUrl}" width="20" height="20" alt="${storeType}" class="store-icon-img">`;
  }

  /**
   * Select a store for pickup
   */
  function selectStore(store) {
    selectedStore = store;
    localStorage.setItem('selected_store', JSON.stringify({
      name: store.name,
      timestamp: Date.now()
    }));

    // Update pickup panel text with selected store info
    updateAvailabilityText();

    renderStoreList();
    closeSidebar();
  }

  /**
   * Update availability text in pickup panel
   */
  function updateAvailabilityText() {
    const pickupCopy = document.querySelector('.pickup-copy p:first-child');
    const pickupCopySub = document.querySelector('.pickup-copy p:last-child');

    if (selectedStore) {
      if (pickupCopy) {
        pickupCopy.textContent = `Available at ${selectedStore.name}`;
      }

      if (pickupCopySub) {
        if (userLocation && userLocation.latitude && selectedStore.distance !== null) {
          pickupCopySub.textContent = `${Math.round(selectedStore.distance)} miles away`;
        } else {
          pickupCopySub.textContent = `Store selected`;
        }
      }
    } else {
      if (pickupCopy) {
        pickupCopy.textContent = `Available near you`;
      }
      if (pickupCopySub) {
        pickupCopySub.textContent = `Select a store to check availability`;
      }
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // CRITICAL: Immediately hide sidebar to prevent flash
  // This runs BEFORE DOMContentLoaded to ensure sidebar is never visible
  (function hideSidebarImmediately() {
    const overlay = document.getElementById('store-overlay');
    const sidebar = document.getElementById('store-sidebar');
    if (overlay) {
      overlay.classList.remove('active', 'is--visible');
      overlay.classList.add('is--hidden');
      overlay.style.opacity = '0';
      overlay.style.visibility = 'hidden';
    }
    if (sidebar) {
      sidebar.classList.remove('active');
      sidebar.style.transform = 'translateX(100%)';
    }
  })();

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPurchaseTypeToggle);
  } else {
    initPurchaseTypeToggle();
  }

  // Expose helper functions globally for debugging
  window.PurchaseToggleDebug = {
    getLocation: () => userLocation,
    getStores: () => storeData,
    render: renderStoreList
  };
})();
